/**
 * Schema engine for `format_specific_data`.
 *
 * A template announces its inputs as a JSON Schema (2020-12, top level always `type: object`),
 * delivered by `template_capabilities(template).format_specific_data_schema`. This module turns
 * that schema plus the current values into everything a form needs — which fields apply, how they
 * are grouped and ordered, their prefill, their validation and the object to submit — without
 * knowing any template or key by name. Pure TypeScript, no Angular.
 *
 * Supported keywords:
 *  - `properties`, `required`, `default`, `type`, `enum`, `const`, `maxLength`,
 *    `minimum`/`maximum`/`exclusiveMinimum`/`exclusiveMaximum`, `format` (date, date-time),
 *    `items.enum` (multiselect), `items.type: string` (tags), `minItems`.
 *  - `if`/`then`/`else`, at top level and inside `allOf` (and inside a `then`/`else` branch).
 *    `if` is evaluated with a small generic validator: const, enum, type, properties (nested),
 *    required, not, allOf/anyOf/oneOf, minimum/maximum, minItems/maxItems.
 *    A branch may add properties (shown only while the branch applies), add `required`, and
 *    narrow a property (its subschema keywords are merged over the base one, e.g. `maximum`).
 *  - Nested objects, one level deep: an object property with `properties` is a sub-group of its
 *    own fields, resolved (including its own if/then) against the object's own values. The
 *    submitted value keeps the nested shape. A missing or empty object counts as absent.
 *  - `x-ui` per property (see {@link XUi}). `widget: "ladder"` hands the value to the encoding
 *    ladder editor; a null or empty ladder is left out.
 *
 * Only keys the schema declares (in its base or an active branch) are submitted, at both levels,
 * whatever `additionalProperties` says. Properties no widget can edit (deeper objects, `anyOf`
 * unions, untyped values) are not rendered; a value already present is passed through, and if
 * one is required the form reports a form-level error rather than inventing a control.
 * `pattern` and `minLength` are not checked.
 */

export type JsonSchema = { [k: string]: any };
export type FsdValues = { [key: string]: any };

export type Widget =
  'switch' | 'select' | 'multiselect' | 'date' | 'datetime' | 'day' | 'text' | 'textarea' | 'number' | 'tags'
  | 'ladder' | 'object';

/** Widgets `x-ui.widget` may name. 'object' is inferred only. */
const WIDGETS: Widget[] = ['switch', 'select', 'multiselect', 'date', 'datetime', 'text', 'textarea', 'number', 'tags', 'ladder'];

export interface XUi {
  label?: string;
  group?: string;
  order?: number;
  widget?: Widget;
  /** ISO 8601 duration, e.g. P2M: the default is today plus this offset. */
  default_offset?: string;
  generate?: 'random_int';
  help?: string;
  hidden?: boolean;
  /** Rendered only after the card's "Advanced" toggle is switched on. */
  advanced?: boolean;
  enum_labels?: { [value: string]: string };
  /** Suggested values: rendered as a select, but validation still follows the schema only. */
  options?: any[];
  /**
   * A maximum derived from a sibling at the same object level. `{pow2_minus_1: "bits"}` means
   * 2^bits - 1, capped at Number.MAX_SAFE_INTEGER. Unknown operators are ignored (warned once).
   */
  max_from?: { [operator: string]: string };
  /** Label of the leading empty entry of an optional select (default "Default"). */
  empty_label?: string;
  /**
   * Output format of a date (tokens D, DD, M, MM, YYYY; default YYYY-MM-DD). The input stays a
   * date picker; a value already in this format parses back.
   */
  date_format?: string;
  /**
   * On a date-time field: offer only a day (`<input type="date">`). Picking one writes that UTC day's
   * first second (`start`, 00:00:00Z) or last second (`end`, 23:59:59Z, inclusive); a value nobody
   * edited is written back exactly as given.
   */
  day?: 'start' | 'end';
  /**
   * Leave the key out of the submitted value while it equals the schema `default` (arrays compared
   * as sets), so the job follows the template's own default if that ever changes.
   */
  omit_when_default?: boolean;
  /** Sibling keys this one only acts together with (e.g. a title card's URL and duration). */
  requires?: string[];
  /**
   * Preselect `value` while the field is untouched and the source video is at least
   * `min_source_duration_seconds` long; otherwise the field keeps its default.
   */
  auto_select?: { value: any; min_source_duration_seconds: number };
}

export interface FsdOption {
  value: any;
  label: string;
}

export interface FsdField {
  key: string;
  /** Dotted path from the root, e.g. `stardustmark.wm_id`. Errors and bounds are keyed by it. */
  path: string;
  /** Effective subschema (base merged with every active branch). */
  schema: JsonSchema;
  label: string;
  group: string;
  order: number;
  /** null when no widget can edit the value (see module doc). */
  widget: Widget | null;
  help?: string;
  hidden: boolean;
  /** JSON Schema readOnly: shown but not editable; always submits the schema default when it has one. */
  readOnly: boolean;
  advanced: boolean;
  required: boolean;
  /** Integer/number type, `integer` true when only whole numbers are allowed. */
  integer: boolean;
  minimum?: number;
  maximum?: number;
  maxLength?: number;
  options: FsdOption[];
  xui: XUi;
  /** Visible fields of an 'object' widget, ordered; empty otherwise. */
  children: FsdField[];
}

export interface FsdGroup {
  name: string;
  fields: FsdField[];
}

export interface EffectiveSchema {
  properties: { [key: string]: JsonSchema };
  required: string[];
  additionalProperties: boolean;
}

export type FieldErrors = { [path: string]: string[] };

// ---------------------------------------------------------------------------------------------
// Conditionals

function isObject(v: any): v is { [k: string]: any } {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepEqual(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function typeMatches(type: string, v: any): boolean {
  switch (type) {
    case 'null':
      return v === null;
    case 'boolean':
      return typeof v === 'boolean';
    case 'integer':
      return typeof v === 'number' && Number.isInteger(v);
    case 'number':
      return typeof v === 'number' && !isNaN(v);
    case 'string':
      return typeof v === 'string';
    case 'array':
      return Array.isArray(v);
    case 'object':
      return isObject(v);
    default:
      return true;
  }
}

/**
 * Whether `value` satisfies `schema`, for the keywords an `if` uses. Unknown keywords pass, as
 * JSON Schema treats them.
 */
export function matches(schema: any, value: any): boolean {
  if (schema === true || schema === undefined) {
    return true;
  }
  if (schema === false) {
    return false;
  }
  if ('const' in schema && !deepEqual(schema.const, value)) {
    return false;
  }
  if (Array.isArray(schema.enum) && !schema.enum.some((e: any) => deepEqual(e, value))) {
    return false;
  }
  if (schema.type !== undefined) {
    const types: string[] = Array.isArray(schema.type) ? schema.type : [schema.type];
    if (!types.some(t => typeMatches(t, value))) {
      return false;
    }
  }
  if (typeof value === 'number') {
    if (typeof schema.minimum === 'number' && value < schema.minimum) return false;
    if (typeof schema.maximum === 'number' && value > schema.maximum) return false;
    if (typeof schema.exclusiveMinimum === 'number' && value <= schema.exclusiveMinimum) return false;
    if (typeof schema.exclusiveMaximum === 'number' && value >= schema.exclusiveMaximum) return false;
  }
  if (Array.isArray(value)) {
    if (typeof schema.minItems === 'number' && value.length < schema.minItems) return false;
    if (typeof schema.maxItems === 'number' && value.length > schema.maxItems) return false;
  }
  if (isObject(value)) {
    if (Array.isArray(schema.required) && !schema.required.every((k: string) => k in value)) {
      return false;
    }
    if (isObject(schema.properties)) {
      for (const [k, sub] of Object.entries(schema.properties)) {
        if (k in value && !matches(sub, value[k])) {
          return false;
        }
      }
    }
  }
  if (schema.not !== undefined && matches(schema.not, value)) {
    return false;
  }
  if (Array.isArray(schema.allOf) && !schema.allOf.every((s: any) => matches(s, value))) {
    return false;
  }
  if (Array.isArray(schema.anyOf) && !schema.anyOf.some((s: any) => matches(s, value))) {
    return false;
  }
  if (Array.isArray(schema.oneOf) && schema.oneOf.filter((s: any) => matches(s, value)).length !== 1) {
    return false;
  }
  return true;
}

/** Folds a (base or branch) schema's properties/required into `acc`, then its own conditionals. */
function collect(schema: JsonSchema, values: FsdValues, acc: EffectiveSchema): void {
  if (!isObject(schema)) {
    return;
  }
  if (isObject(schema.properties)) {
    for (const [k, sub] of Object.entries(schema.properties)) {
      acc.properties[k] = isObject(acc.properties[k]) && isObject(sub)
        ? {...acc.properties[k], ...sub}
        : sub as JsonSchema;
    }
  }
  if (Array.isArray(schema.required)) {
    for (const k of schema.required) {
      if (!acc.required.includes(k)) acc.required.push(k);
    }
  }
  const conditionals: JsonSchema[] = [schema, ...(Array.isArray(schema.allOf) ? schema.allOf : [])];
  for (const c of conditionals) {
    if (c !== schema) {
      // An allOf entry may itself carry properties/required as well as if/then.
      const {if: _i, then: _t, else: _e, ...plain} = c;
      collect(plain, values, acc);
    }
    if (c.if === undefined) {
      continue;
    }
    const branch = matches(c.if, values) ? c.then : c.else;
    if (branch !== undefined) {
      collect(branch, values, acc);
    }
  }
}

/** The schema that applies to `values`: base properties plus every active then/else branch. */
export function resolveSchema(schema: JsonSchema | null | undefined, values: FsdValues): EffectiveSchema {
  const acc: EffectiveSchema = {
    properties: {},
    required: [],
    additionalProperties: schema?.additionalProperties !== false,
  };
  if (schema) {
    collect(schema, values ?? {}, acc);
  }
  return acc;
}

/** Whether the schema declares no input at all (no card is rendered). */
export function isEmptySchema(schema: JsonSchema | null | undefined): boolean {
  if (!schema) {
    return true;
  }
  const all = allPropertyKeys(schema);
  return all.size === 0;
}

/** Every property key the schema can ever declare, in any branch. */
export function allPropertyKeys(schema: JsonSchema): Set<string> {
  const keys = new Set<string>();
  const walk = (s: any) => {
    if (!isObject(s)) return;
    if (isObject(s.properties)) Object.keys(s.properties).forEach(k => keys.add(k));
    for (const sub of [s.then, s.else, ...(Array.isArray(s.allOf) ? s.allOf : [])]) walk(sub);
  };
  walk(schema);
  return keys;
}

// ---------------------------------------------------------------------------------------------
// Fields

function primaryType(schema: JsonSchema): string | undefined {
  const t = schema.type;
  if (Array.isArray(t)) {
    return t.find((x: string) => x !== 'null');
  }
  return t;
}

function declaresProperties(schema: JsonSchema): boolean {
  return allPropertyKeys(schema).size > 0;
}

export function inferWidget(schema: JsonSchema): Widget | null {
  const xui: XUi = schema['x-ui'] ?? {};
  if ((xui.day === 'start' || xui.day === 'end') && schema.format === 'date-time') {
    return 'day';
  }
  if (xui.widget && WIDGETS.includes(xui.widget)) {
    return xui.widget;
  }
  const type = primaryType(schema);
  if (Array.isArray(schema.enum) || Array.isArray(xui.options)) {
    return 'select';
  }
  if (type === 'boolean') {
    return 'switch';
  }
  if (type === 'array') {
    if (Array.isArray(schema.items?.enum)) return 'multiselect';
    if (primaryType(schema.items ?? {}) === 'string') return 'tags';
    return null;
  }
  if (type === 'object') {
    return declaresProperties(schema) ? 'object' : null;
  }
  if (type === 'string') {
    if (schema.format === 'date') return 'date';
    if (schema.format === 'date-time') return 'datetime';
    return 'text';
  }
  if (type === 'integer' || type === 'number') {
    return 'number';
  }
  return null;
}

function optionLabel(value: any, labels: { [v: string]: string } | undefined): string {
  const key = value === null ? 'null' : String(value);
  if (labels && key in labels) {
    return labels[key];
  }
  return value === null || value === '' ? '—' : String(value);
}

/**
 * Options of a select. An optional select whose values offer no "none" (null or '') gets a leading
 * empty entry:
 * - without a default: "Default" (or x-ui.empty_label) with value undefined, so the key is left out
 *   and the template's own default applies (e.g. dyn_subtitle_position);
 * - with a default and an x-ui.empty_label: that label with value null -- the schema names "none"
 *   as a choice of its own (e.g. dfe_ca_bits "Disabled"), and null, unlike an absent key, is not
 *   prefilled with the default again when a PO is restored.
 */
function selectOptions(widget: Widget | null, values: any[], xui: XUi, required: boolean, hasDefault: boolean): FsdOption[] {
  const options = values.map(v => ({value: v, label: optionLabel(v, xui.enum_labels)}));
  if (widget !== 'select' || required || values.some(v => v === null || v === '')) {
    return options;
  }
  if (!hasDefault) {
    options.unshift({value: undefined, label: xui.empty_label ?? 'Default'});
  } else if (xui.empty_label) {
    options.unshift({value: null, label: xui.empty_label});
  }
  return options;
}

function sortFields(fields: FsdField[]): FsdField[] {
  return fields
    .map((f, i) => ({f, i}))
    .sort((a, b) => (a.f.order - b.f.order) || (a.i - b.i))
    .map(({f}) => f);
}

function isRenderable(f: FsdField): boolean {
  return !f.hidden && f.widget !== null;
}

const warnedOperators = new Set<string>();

/**
 * The maximum `x-ui.max_from` derives from the sibling values, or undefined when there is none
 * (no max_from, unknown operator, sibling missing or not a non-negative integer). Computed with
 * BigInt (2^256 exceeds Number precision) and capped at Number.MAX_SAFE_INTEGER, since the
 * form's values are JavaScript numbers.
 */
export function derivedMaximum(maxFrom: any, siblings: FsdValues): number | undefined {
  if (!isObject(maxFrom)) return undefined;
  let result: number | undefined;
  for (const [op, sibling] of Object.entries(maxFrom)) {
    if (op !== 'pow2_minus_1') {
      if (!warnedOperators.has(op)) {
        warnedOperators.add(op);
        console.warn(`fsd-schema: unknown x-ui.max_from operator "${op}" ignored`);
      }
      continue;
    }
    const n = siblings?.[sibling as string];
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 0) continue;
    const big = (BigInt(1) << BigInt(n)) - BigInt(1);
    const max = big > BigInt(Number.MAX_SAFE_INTEGER) ? Number.MAX_SAFE_INTEGER : Number(big);
    result = result === undefined ? max : Math.min(result, max);
  }
  return result;
}

/** A field's options plus the current value when it is not among them, so nothing is silently changed. */
export function optionsWithValue(field: FsdField, value: any): FsdOption[] {
  if (value === undefined || field.widget !== 'select' || field.options.some(o => deepEqual(o.value, value))) {
    return field.options;
  }
  return [...field.options, {value, label: optionLabel(value, field.xui.enum_labels)}];
}

function buildField(key: string, prefix: string, schema: JsonSchema, required: boolean, ownValue: any,
                    siblings: FsdValues): FsdField {
  const xui: XUi = isObject(schema['x-ui']) ? schema['x-ui'] : {};
  let widget = inferWidget(schema);
  if (widget === 'object' && prefix !== '') {
    widget = null; // one level deep only
  }
  const path = prefix + key;
  const type = primaryType(schema);
  const enumValues: any[] | undefined = widget === 'multiselect' ? schema.items?.enum
    : Array.isArray(xui.options) ? xui.options : schema.enum;
  const minimum = typeof schema.minimum === 'number' ? schema.minimum
    : typeof schema.exclusiveMinimum === 'number' ? schema.exclusiveMinimum + (type === 'integer' ? 1 : 0) : undefined;
  const schemaMaximum = typeof schema.maximum === 'number' ? schema.maximum
    : typeof schema.exclusiveMaximum === 'number' ? schema.exclusiveMaximum - (type === 'integer' ? 1 : 0) : undefined;
  const derived = derivedMaximum(xui.max_from, siblings);
  const maximum = derived === undefined ? schemaMaximum
    : schemaMaximum === undefined ? derived : Math.min(schemaMaximum, derived);
  return {
    key,
    path,
    schema,
    label: xui.label ?? schema.title ?? key,
    group: xui.group ?? '',
    order: typeof xui.order === 'number' ? xui.order : Number.MAX_SAFE_INTEGER,
    widget,
    help: xui.help ?? undefined,
    hidden: xui.hidden === true,
    readOnly: schema.readOnly === true,
    advanced: xui.advanced === true,
    required,
    integer: type === 'integer',
    minimum,
    maximum,
    maxLength: typeof schema.maxLength === 'number' ? schema.maxLength : undefined,
    options: selectOptions(widget, enumValues ?? [], xui, required, 'default' in schema),
    xui,
    children: widget === 'object'
      ? sortFields(fieldsAt(schema, isObject(ownValue) ? ownValue : {}, path + '.').filter(isRenderable))
      : [],
  };
}

function fieldsAt(schema: JsonSchema | null | undefined, values: FsdValues, prefix: string): FsdField[] {
  const eff = resolveSchema(schema, values);
  return Object.entries(eff.properties)
    .filter(([, sub]) => isObject(sub))
    .map(([k, sub]) => buildField(k, prefix, sub, eff.required.includes(k), values?.[k], values));
}

/**
 * Every top-level field applicable to `values`, in schema order. Includes hidden and unsupported
 * ones. An object field's `children` holds its visible nested fields.
 */
export function applicableFields(schema: JsonSchema | null | undefined, values: FsdValues): FsdField[] {
  return fieldsAt(schema, values ?? {}, '');
}

/**
 * Fields to render, grouped and ordered. Groups keep the order of their first field (by x-ui
 * order, then schema order); fields within a group sort by `x-ui.order` ascending, ties in
 * schema order. The unnamed group ('') is a group like any other. An object field stays in its
 * own group and carries its sub-fields as `children`.
 */
export function visibleGroups(schema: JsonSchema | null | undefined, values: FsdValues): FsdGroup[] {
  const groups: FsdGroup[] = [];
  for (const f of sortFields(applicableFields(schema, values).filter(isRenderable))) {
    let g = groups.find(x => x.name === f.group);
    if (!g) {
      g = {name: f.group, fields: []};
      groups.push(g);
    }
    g.fields.push(f);
  }
  return groups;
}

// ---------------------------------------------------------------------------------------------
// Dates

/** Adds an ISO 8601 duration (PnYnMnWnDTnHnMnS) to `base`. Returns null for an invalid one. */
export function addIsoDuration(base: Date, duration: string): Date | null {
  const m = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(duration ?? '');
  if (!m || duration === 'P' || duration.endsWith('T')) {
    return null;
  }
  const [y, mo, w, d, h, mi, s] = m.slice(1).map(x => (x ? parseInt(x, 10) : 0));
  const out = new Date(base.getTime());
  out.setFullYear(out.getFullYear() + y, out.getMonth() + mo, out.getDate() + w * 7 + d);
  out.setHours(out.getHours() + h, out.getMinutes() + mi, out.getSeconds() + s);
  return out;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar date as YYYY-MM-DD. */
export function formatDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The UTC day (YYYY-MM-DD) a `day` field shows for its ISO value; '' when there is none. */
export function utcDayOf(v: any): string {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(v)) return '';
  // The day of the instant in UTC, not the string's own date: 23:30-04:00 on the 15th is the 16th UTC
  // (Percival). A value without a zone, or one Date cannot parse, shows its written date.
  const d = /T.*(Z|[+-]\d{2}:?\d{2})$/.test(v) ? new Date(v) : null;
  return d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : v.slice(0, 10);
}

/** A picked day (YYYY-MM-DD) → the ISO value of a `day` field: its first or last second, UTC. */
export function dayToUtc(day: string, bound: 'start' | 'end'): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day ?? '')) return undefined;
  return `${day}T${bound === 'start' ? '00:00:00' : '23:59:59'}Z`;
}

/**
 * Adds x-ui hints to the fields at the given paths (dotted for nested objects), wherever the schema
 * declares them -- also in if/then/else and allOf branches. A form passes these for fields its
 * template's schema does not hint yet; the schema itself is not changed.
 */
export function withFieldHints(schema: JsonSchema | null, hints: { [path: string]: XUi }): JsonSchema | null {
  if (!schema || !Object.keys(hints).length) return schema;
  const walk = (node: any, prefix: string): any => {
    if (!isObject(node)) return node;
    const out: any = {...node};
    if (isObject(node.properties)) {
      out.properties = {};
      for (const [k, v] of Object.entries<any>(node.properties)) {
        let child = walk(v, prefix + k + '.');
        const hint = hints[prefix + k];
        if (hint && isObject(child)) child = {...child, 'x-ui': {...(child['x-ui'] ?? {}), ...hint}};
        out.properties[k] = child;
      }
    }
    for (const key of ['if', 'then', 'else']) {
      if (isObject(node[key])) out[key] = walk(node[key], prefix);
    }
    for (const key of ['allOf', 'anyOf', 'oneOf']) {
      if (Array.isArray(node[key])) out[key] = node[key].map((b: any) => walk(b, prefix));
    }
    return out;
  };
  return walk(schema, '');
}

/**
 * Local date-time as YYYY-MM-DDTHH:MM, with :SS when the seconds are not zero (the value an
 * `<input type="datetime-local" step="1">` holds) -- so a restored 23:59:59Z round-trips unchanged.
 */
export function formatDateTime(d: Date): string {
  const seconds = d.getSeconds() ? `:${pad(d.getSeconds())}` : '';
  return `${formatDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}${seconds}`;
}

const LOCAL_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** A local datetime-input value → ISO 8601 UTC with Z, seconds precision. Other values unchanged. */
export function localToUtc(v: any): any {
  if (typeof v !== 'string' || !LOCAL_DATETIME.test(v)) return v;
  const d = new Date(v); // no zone designator: parsed as local time
  return isNaN(d.getTime()) ? v : d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** An ISO 8601 value with a zone (Z or ±hh:mm) → the local datetime-input value. */
export function utcToLocal(v: any): any {
  if (typeof v !== 'string' || LOCAL_DATETIME.test(v) || !/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/.test(v)) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : formatDateTime(d);
}

const DATE_TOKENS = /YYYY|DD|D|MM|M/g;

/** Formats an ISO date (YYYY-MM-DD) with tokens D, DD, M, MM, YYYY. Other values unchanged. */
export function formatDateFormat(v: any, fmt: string): any {
  const m = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v) : null;
  if (!m) return v;
  const [y, mo, d] = [m[1], parseInt(m[2], 10), parseInt(m[3], 10)];
  return fmt.replace(DATE_TOKENS, t => ({YYYY: y, MM: pad(mo), M: String(mo), DD: pad(d), D: String(d)} as any)[t]);
}

/** Parses a date written with `fmt` back to YYYY-MM-DD; null when it does not match. */
/** How dates were typed before the schema form (e.g. a NiceMedia license_expiration_date '8.12.2026'). */
const LEGACY_DATE_FORMAT = 'D.M.YYYY';

export function parseDateFormat(v: string, fmt: string): string | null {
  const order: string[] = [];
  const pattern = fmt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(DATE_TOKENS, t => {
    order.push(t[0]);
    return t === 'YYYY' ? '(\\d{4})' : t.length === 2 ? '(\\d{2})' : '(\\d{1,2})';
  });
  const m = new RegExp(`^${pattern}$`).exec(v);
  if (!m) return null;
  const parts: { [k: string]: number } = {};
  order.forEach((k, i) => parts[k] = parseInt(m[i + 1], 10));
  if (!parts['Y'] || !parts['M'] || !parts['D']) return null;
  return `${parts['Y']}-${pad(parts['M'])}-${pad(parts['D'])}`;
}

// ---------------------------------------------------------------------------------------------
// Prefill

export type Rng = () => number;

/**
 * A random integer in [min, max]; bounds default to 0 and 2^31-1. The range is capped at
 * Number.MAX_SAFE_INTEGER (values beyond it are not exact JavaScript numbers).
 */
export function randomInt(min: number | undefined, max: number | undefined, rng: Rng = Math.random): number {
  const lo = Math.max(Math.ceil(min ?? 0), Number.MIN_SAFE_INTEGER);
  const hi = Math.min(Math.floor(max ?? 2 ** 31 - 1), Number.MAX_SAFE_INTEGER);
  return Math.min(hi, lo + Math.floor(rng() * (hi - lo + 1)));
}

function clone<T>(v: T): T {
  return v === undefined ? v : JSON.parse(JSON.stringify(v));
}

function initialValue(field: FsdField, now: Date, rng: Rng): any {
  const xui = field.xui;
  if (xui.default_offset) {
    const d = addIsoDuration(now, xui.default_offset);
    if (d) {
      return field.widget === 'datetime' ? formatDateTime(d) : formatDate(d);
    }
  }
  if (xui.generate === 'random_int') {
    return randomInt(field.minimum, field.maximum, rng);
  }
  if ('default' in field.schema) {
    const v = clone(field.schema.default);
    return field.widget === 'datetime' ? utcToLocal(v) : v;
  }
  return undefined;
}

function fillPass(schema: JsonSchema | null | undefined, values: FsdValues, now: Date, rng: Rng,
                  computed: boolean): boolean {
  let changed = false;
  for (const f of fieldsAt(schema, values, '')) {
    if (f.widget === 'object') continue;
    const isComputed = !!(f.xui.default_offset || f.xui.generate);
    if (values[f.key] === undefined && (computed || !isComputed)) {
      const v = initialValue(f, now, rng);
      if (v !== undefined) {
        values[f.key] = v;
        changed = true;
      }
    }
  }
  return changed;
}

/** Prefill of one level: plain defaults until they settle, then computed values. */
function prefillLevel(schema: JsonSchema | null | undefined, values: FsdValues, now: Date, rng: Rng): void {
  for (const computed of [false, true]) {
    for (let pass = 0; pass < 10; pass++) {
      if (!fillPass(schema, values, now, rng, computed)) break;
    }
  }
  for (const f of fieldsAt(schema, values, '')) {
    // readOnly: whatever was given (a restored PO, an import), the form shows and keeps the default.
    if (f.readOnly && 'default' in f.schema) values[f.key] = clone(f.schema.default);
    if (f.widget === 'datetime' && values[f.key] !== undefined) values[f.key] = utcToLocal(values[f.key]);
    if (f.widget === 'date' && typeof values[f.key] === 'string') {
      // The input shows only YYYY-MM-DD. A value in the declared x-ui.date_format, or in the dotted
      // D.M.YYYY that POs saved before the schema form carry (NiceMedia), is read into that form.
      values[f.key] = parseDateFormat(values[f.key], f.xui.date_format ?? LEGACY_DATE_FORMAT) ?? values[f.key];
    }
  }
}

/**
 * Initial values for every applicable field: `default`, `x-ui.default_offset` or
 * `x-ui.generate`. Values in `given` win; a datetime given in UTC becomes the local input value.
 * A default that switches a condition on prefills that branch's fields too, and generated values
 * see the bounds the settled defaults select. An applicable nested object is prefilled from its
 * own fields (and stays absent when none has a default).
 */
export function prefill(schema: JsonSchema | null | undefined, given: FsdValues = {},
                        now: Date = new Date(), rng: Rng = Math.random): FsdValues {
  const values: FsdValues = {...given};
  prefillLevel(schema, values, now, rng);
  for (const f of fieldsAt(schema, values, '')) {
    if (f.widget !== 'object') continue;
    const own: FsdValues = isObject(values[f.key]) ? {...values[f.key]} : {};
    prefillLevel(f.schema, own, now, rng);
    if (Object.keys(own).length > 0 || isObject(values[f.key])) {
      values[f.key] = own;
    }
  }
  // A nested prefill may have satisfied a top-level condition.
  prefillLevel(schema, values, now, rng);
  return values;
}

/** Top-level keys of the fields that apply to these values (base properties and active branches). */
export function applicableKeys(schema: JsonSchema | null | undefined, values: FsdValues): Set<string> {
  return new Set(applicableFields(schema, values).map(f => f.key));
}

/**
 * Prefill the fields a value change just brought into play -- a branch that became active, e.g.
 * `watermark` set to `stardustmark` creating the `stardustmark` object -- the same way `prefill`
 * does for a fresh form: defaults (also inside a new nested object), offsets, generated values.
 * Only keys that were not applicable before and have no value are touched, so a field the operator
 * emptied is never refilled.
 */
export function prefillActivated(schema: JsonSchema | null | undefined, before: Set<string>, values: FsdValues,
                                 now: Date = new Date(), rng: Rng = Math.random): FsdValues {
  const fresh = [...applicableKeys(schema, values)].filter(k => !before.has(k) && values[k] === undefined);
  if (!fresh.length) return values;
  const filled = prefill(schema, values, now, rng);
  const out = {...values};
  for (const k of fresh) {
    if (filled[k] !== undefined) out[k] = filled[k];
  }
  return out;
}

export type Bounds = { [path: string]: [number | undefined, number | undefined] };

function syncLevel(fields: FsdField[], values: FsdValues, previous: Bounds, bounds: Bounds, rng: Rng): FsdValues {
  const out = {...values};
  for (const f of fields) {
    if (f.widget === 'object') {
      if (isObject(out[f.key])) {
        const own = fieldsAt(f.schema, out[f.key], f.path + '.');
        out[f.key] = syncLevel(own, out[f.key], previous, bounds, rng);
      }
      continue;
    }
    if (f.xui.generate !== 'random_int') continue;
    bounds[f.path] = [f.minimum, f.maximum];
    const prev = previous[f.path];
    const moved = prev !== undefined && (prev[0] !== f.minimum || prev[1] !== f.maximum);
    // A value that still fits the moved bounds is kept: it may be one the operator typed or
    // imported, and only an ID the new range cannot hold has to be drawn again.
    const v = out[f.key];
    const fits = typeof v === 'number' && (f.minimum === undefined || v >= f.minimum) && (f.maximum === undefined || v <= f.maximum);
    if (v === undefined || v === null || v === '' || (moved && !fits)) {
      out[f.key] = randomInt(f.minimum, f.maximum, rng);
    }
  }
  return out;
}

/**
 * Keeps `x-ui.generate: random_int` fields (at both levels) in step with their effective bounds.
 * A field is (re)generated when it has no value, or when its bounds differ from `previous` (a
 * sibling changed which branch narrows it) and its value no longer fits them. Returns the new values and the bounds to pass next.
 */
/**
 * Applies `x-ui.auto_select` (at both levels) to fields not in `touched` (paths): the auto value
 * when `sourceDurationSeconds` is known and at least the threshold, else the field's default.
 * A touched field is never changed. No source duration (e.g. no video track) selects nothing.
 */
export function applyAutoSelect(schema: JsonSchema | null | undefined, values: FsdValues,
                                touched: Set<string>, sourceDurationSeconds: number | null | undefined): FsdValues {
  const level = (fields: FsdField[], vals: FsdValues): FsdValues => {
    let out = vals;
    for (const f of fields) {
      if (f.widget === 'object') {
        if (isObject(out[f.key])) {
          out = {...out, [f.key]: level(fieldsAt(f.schema, out[f.key], f.path + '.'), out[f.key])};
        }
        continue;
      }
      const rule = f.xui.auto_select;
      if (!isObject(rule) || touched.has(f.path)) continue;
      const on = typeof sourceDurationSeconds === 'number' && typeof rule.min_source_duration_seconds === 'number'
        && sourceDurationSeconds >= rule.min_source_duration_seconds;
      const next = on ? clone(rule.value) : clone(f.schema.default);
      if (!deepEqual(out[f.key], next)) out = {...out, [f.key]: next};
    }
    return out;
  };
  return level(applicableFields(schema, values ?? {}), values ?? {});
}

export function syncGenerated(schema: JsonSchema | null | undefined, values: FsdValues,
                              previous: Bounds = {}, rng: Rng = Math.random): { values: FsdValues; bounds: Bounds } {
  const bounds: Bounds = {};
  const out = syncLevel(applicableFields(schema, values), values ?? {}, previous, bounds, rng);
  return {values: out, bounds};
}

// ---------------------------------------------------------------------------------------------
// Submit + validation

function isBlank(v: any): boolean {
  return v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
}

/** No object, or one whose every value is blank: what clearing all of an object's inputs leaves. */
function isEmptyObject(v: any): boolean {
  return !isObject(v) || Object.values(v).every(x => isBlank(x) || (isObject(x) && isEmptyObject(x)));
}

function coerce(field: FsdField, v: any): any {
  if (field.widget === 'number' && typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return isNaN(n) ? v : n;
  }
  return v;
}

/**
 * Ladder-widget rule: a ladder's `drm_streamtypes` (its DRM key tiers by width) describe nothing
 * when its level is not encrypted (`encrypt` sibling not true), so they are left out. A copy; the
 * form value keeps its tiers.
 */
export function ladderForEncryption(ladder: any, encrypted: boolean): any {
  if (encrypted || !isObject(ladder) || !('drm_streamtypes' in ladder)) {
    return ladder;
  }
  const {drm_streamtypes: _drop, ...rest} = ladder;
  return rest;
}

function normalizeLevel(schema: JsonSchema | null | undefined, values: FsdValues): FsdValues {
  const out: FsdValues = {};
  for (const f of fieldsAt(schema, values ?? {}, '')) {
    let v = values?.[f.key];
    if ((v === undefined && f.hidden || f.readOnly) && 'default' in f.schema) {
      v = clone(f.schema.default);
    }
    if (f.widget === 'object') {
      if (isObject(v)) {
        const nested = normalizeLevel(f.schema, v);
        if (Object.keys(nested).length) out[f.key] = nested;
      }
      continue;
    }
    if (f.widget === 'ladder') {
      if (!isEmptyObject(v)) out[f.key] = ladderForEncryption(v, values?.['encrypt'] === true);
      continue;
    }
    v = coerce(f, v);
    if (f.widget === 'datetime') v = localToUtc(v);
    if (f.widget === 'date' && f.xui.date_format) v = formatDateFormat(v, f.xui.date_format);
    // An empty input is left out entirely: never '' or null (a select's chosen null/'' is a value).
    if (v === undefined || ((v === '' || v === null) && f.widget !== 'select')
      || (f.widget === 'tags' && Array.isArray(v) && v.length === 0)) {
      continue;
    }
    if (f.xui.omit_when_default && 'default' in f.schema && equalsDefault(v, f.schema.default)) {
      continue;
    }
    out[f.key] = v;
  }
  return out;
}

/** Value equality for `omit_when_default`: arrays as sets, everything else by JSON. */
export function equalsDefault(v: any, dflt: any): boolean {
  if (Array.isArray(v) && Array.isArray(dflt)) {
    const a = new Set(v.map(x => JSON.stringify(x)));
    const b = new Set(dflt.map(x => JSON.stringify(x)));
    return a.size === b.size && [...a].every(x => b.has(x));
  }
  return JSON.stringify(v) === JSON.stringify(dflt);
}

/**
 * The object to submit: only keys the schema declares for these values (base properties and
 * active branches, at both levels), hidden ones with their value or default, numbers coerced,
 * datetimes in UTC, blanks dropped, empty nested objects and null/empty ladders left out.
 */
export function normalizeForSubmit(schema: JsonSchema | null | undefined, values: FsdValues): FsdValues {
  return normalizeLevel(schema, values ?? {});
}

/** Errors for one scalar field's value, as human-readable messages. */
export function validateField(field: FsdField, raw: any): string[] {
  const v = coerce(field, raw);
  const errors: string[] = [];
  // null counts as a value only where the schema admits it (a null enum entry or type).
  const nullAllowed = (Array.isArray(field.schema.enum) && field.schema.enum.includes(null))
    || (Array.isArray(field.schema.type) && field.schema.type.includes('null'));
  // An emptied optional multiselect is a choice, not an absent value, when the schema sets a minimum.
  if (!field.required && Array.isArray(v) && v.length === 0 && typeof field.schema.minItems === 'number' && field.schema.minItems > 0) {
    return [`Choose at least ${field.schema.minItems}.`];
  }
  if (isBlank(v) || (v === null && !nullAllowed)) {
    if (field.required) {
      errors.push('This field is required.');
    }
    return errors;
  }
  if (!matches({type: field.schema.type}, v)) {
    errors.push(field.widget === 'number'
      ? (field.integer ? 'Enter a whole number.' : 'Enter a number.')
      : `Expected ${JSON.stringify(field.schema.type)}.`);
    return errors;
  }
  if (Array.isArray(field.schema.enum) && !field.schema.enum.some((e: any) => deepEqual(e, v))) {
    errors.push('Choose one of the listed values.');
  }
  if (Array.isArray(v) && Array.isArray(field.schema.items?.enum)
    && !v.every(x => field.schema.items.enum.some((e: any) => deepEqual(e, x)))) {
    errors.push('Choose only listed values.');
  }
  if (Array.isArray(v) && typeof field.schema.minItems === 'number' && v.length < field.schema.minItems) {
    errors.push(`Choose at least ${field.schema.minItems}.`);
  }
  if ('const' in field.schema && !deepEqual(field.schema.const, v)) {
    errors.push(`Must be ${JSON.stringify(field.schema.const)}.`);
  }
  if (typeof v === 'number') {
    if (field.minimum !== undefined && v < field.minimum) errors.push(`The minimum value is ${field.minimum}.`);
    if (field.maximum !== undefined && v > field.maximum) errors.push(`The maximum value is ${field.maximum}.`);
  }
  if (typeof v === 'string' && field.maxLength !== undefined && v.length > field.maxLength) {
    errors.push(`At most ${field.maxLength} characters.`);
  }
  return errors;
}

function validateLevel(fields: FsdField[], values: FsdValues, errors: FieldErrors): void {
  const formLevel = (msg: string) => (errors[''] ??= []).push(msg);
  for (const f of fields) {
    const raw = values?.[f.key];
    if (f.widget === 'object') {
      if (isEmptyObject(raw)) {
        if (f.required) errors[f.path] = ['This field is required.'];
        continue;
      }
      validateLevel(fieldsAt(f.schema, raw, f.path + '.'), raw, errors);
      continue;
    }
    if (f.widget === 'ladder') {
      // The ladder editor validates its content itself; only presence is the schema's business.
      if (f.required && isEmptyObject(raw)) errors[f.path] = ['This field is required.'];
      continue;
    }
    if (f.widget === null) {
      if (f.required && raw === undefined) {
        formLevel(`"${f.label}" is required but cannot be edited in this form.`);
      }
      continue;
    }
    const v = raw === undefined && f.hidden ? f.schema.default : raw;
    const e = validateField(f, v);
    // x-ui.requires: a value here only acts together with these siblings, so they must be filled too.
    if (!isBlank(v) && v !== null && Array.isArray(f.xui.requires)) {
      for (const other of f.xui.requires) {
        const sibling = fields.find(x => x.key === other);
        if (sibling && (isBlank(values?.[other]) || values?.[other] === null)) {
          e.push(`Only takes effect together with "${sibling.label}".`);
        }
      }
    }
    if (e.length) errors[f.path] = e;
  }
}

/** Path → errors for every applicable field with errors (nested as `parent.child`). '' holds form-level errors. */
export function validate(schema: JsonSchema | null | undefined, values: FsdValues): FieldErrors {
  const errors: FieldErrors = {};
  validateLevel(applicableFields(schema, values), values ?? {}, errors);
  return errors;
}

/** Reads a dotted path (one level deep) from values. */
export function getPath(values: FsdValues, path: string): any {
  const [a, b] = path.split('.');
  return b === undefined ? values?.[a] : values?.[a]?.[b];
}

/** Returns a copy of values with the dotted path set (creating the nested object when needed). */
export function setPath(values: FsdValues, path: string, v: any): FsdValues {
  const [a, b] = path.split('.');
  if (b === undefined) return {...values, [a]: v};
  return {...values, [a]: {...(isObject(values?.[a]) ? values[a] : {}), [b]: v}};
}

/**
 * Parses the AWSJSON string (or object) the API returns. null for anything unusable, including
 * JSON null. Tolerates one extra level of encoding -- a resolver that stringifies a value AppSync
 * then serialises again -- since that shipped once (#2762, sannies e2e) and costs nothing here.
 */
export function parseSchema(raw: string | JsonSchema | null | undefined): JsonSchema | null {
  if (raw === null || raw === undefined || raw === '') return null;
  try {
    let s: any = raw;
    for (let i = 0; i < 2 && typeof s === 'string'; i++) {
      s = JSON.parse(s);
    }
    return isObject(s) ? s : null;
  } catch {
    return null;
  }
}
